/**
 * Outsourcing (the coating desk): the reads, the derived lists and the pure rules, ONE copy for the desktop page
 * (pages/CoatingManagementPage.jsx, office and the Shipping hub's operator mode) and the phone Shipping screen's
 * Outsourcing tab (mobile/screens/shipping/OutsourcingTab.jsx). Moved from CoatingManagementPage word for word
 * (2026-10-29, docs/mobile-pwa-plan.md "One copy of every action"); its tests pin the words and requests.
 * The writes are ./coatingActions.js.
 */
import { palletBuckets, palletClosure, palletTickState } from './palletBuckets';

/** Defensive FastAPI error parser: handles plain text, {detail: "..."} and
 *  validation arrays. Mirrors JobQueuePage's shared parser. */
export async function parseServerError(res) {
  let text = '';
  try {
    text = await res.text();
  } catch {
    return `HTTP ${res.status}`;
  }
  let msg = text;
  try {
    const asJson = JSON.parse(text);
    if (asJson && typeof asJson.detail === 'string') {
      msg = asJson.detail;
    } else if (asJson && Array.isArray(asJson.detail)) {
      msg = asJson.detail.map((d) => d.msg || JSON.stringify(d)).join(' | ');
    } else if (asJson && asJson.detail && typeof asJson.detail.message === 'string') {
      msg = asJson.detail.message;     // {code, message, ...} (e.g. a Send's paper_differs)
    }
  } catch {
    // keep raw text
  }
  if (!msg) msg = `HTTP ${res.status}`;
  return typeof msg === 'string' ? msg : String(msg);
}

// ---- Words ------------------------------------------------------------------------------------

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtMoney(v) {
  if (v == null || isNaN(Number(v))) return '—';
  return `$${Number(v).toFixed(2)}`;
}

export function fmtQty(v) {
  const n = Number(v || 0);
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 1000) / 1000);
}

/** Age since an ISO timestamp, with the same staleness convention as
 *  ShippingQueuePage's pickup tab: yellow at 3+ days, red at 7+ days. `level` = 'ok' | 'warn' | 'late'. */
export function ageBadge(iso) {
  if (!iso) return { label: '—', className: 'bg-light text-dark', level: 'ok' };
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return { label: '—', className: 'bg-light text-dark', level: 'ok' };
  const days = ms / 86400000;
  const label = days < 1 ? `${Math.max(0, Math.round(days * 24))}h` : `${Math.floor(days)}d`;
  const className = days >= 7 ? 'bg-danger' : days >= 3 ? 'bg-warning text-dark' : 'bg-light text-dark';
  return { label, className, level: days >= 7 ? 'late' : days >= 3 ? 'warn' : 'ok' };
}

// ---- Staging rules ------------------------------------------------------------------------------

/** A pallet of another order's packing plan (jobq-api tags each pallet with its plan's order).
 *  A job split by allocation sits on both orders' pallets; each order's paper names its own. */
export function isOtherOrderPallet(pl, salesOrderId) {
  return pl.sales_order_id != null && salesOrderId != null
    && Number(pl.sales_order_id) !== Number(salesOrderId);
}

/** A split row whose own order has nothing left to batch (jobq-api `units_remaining` 0 with
 *  pieces packed for another order): the API refuses it, so it is never selectable. */
export function ownShareDone(t) {
  return Array.isArray(t.allocated_away) && t.allocated_away.length > 0
    && t.units_remaining != null && Number(t.units_remaining) <= 0;
}

/** "SO-1139 (93)" for a row's pieces packed for other orders. */
export function allocatedAwayText(t) {
  return (t.allocated_away || [])
    .map((a) => `${a.sales_order_number || `SO #${a.sales_order_id}`} (${fmtQty(a.qty)})`)
    .join(', ');
}

/** A split row's shares still to go on ANOTHER order's paper. The board lists each under the order
 *  it is packed for (2026-10-27), not under the order whose job cut it. A share with no order
 *  stays a note on its own row. */
export function awaySharesToList(t) {
  return (t.allocated_away || []).filter((a) => a.sales_order_id != null && Number(a.remaining) > 0);
}

/** A split row whose own order is done and whose remaining pieces are all listed under their own
 *  orders: nothing left to show under the job's order. */
export function homeRowHidden(t) {
  return ownShareDone(t)
    && !(t.allocated_away || []).some((a) => a.sales_order_id == null && Number(a.remaining) > 0);
}

/** The home row's pallets without the ones holding pieces packed for another order (those pallets
 *  show on that order's share row, and its header makes their paper). */
export function homeRowPallets(t) {
  const away = new Set((t.allocated_away || [])
    .filter((a) => a.sales_order_id != null).map((a) => Number(a.sales_order_id)));
  if (!away.size) return t.pallets;
  return (t.pallets || []).filter((pl) => !(isOtherOrderPallet(pl, t.sales_order_id)
    && away.has(Number(pl.sales_order_id))));
}

/** A share row's pallets: the ones holding pieces packed for that order. */
export function sharePallets(t, a) {
  return (t.pallets || []).filter((pl) => (pl.sales_order_id != null
    ? Number(pl.sales_order_id) === Number(a.sales_order_id)
    : (a.pallets || []).includes(pl.pallet_short_id)));
}

/** One manifest per SO; internal (NULL-SO) tasks group per customer. */
export function groupKeyForTask(t) {
  return t.sales_order_id != null ? `so:${t.sales_order_id}` : `internal:${t.customer_id ?? 'none'}`;
}

/** Only OPOs with at least one task-linked line are coating manifests; other
 *  OPOs (manual office orders, heat treat, ...) still appear on the At Vendor
 *  and History tabs but carry no task-completion affordances. */
export function isManifest(opo) {
  return (opo.lines || []).some((l) => l.job_task_id);
}

export const DEAD_TASK_STATUSES = ['Voided', 'Cancelled'];

/** A Draft line whose task was voided / cancelled: it must come off before the paper is sent. */
export function isDeadLine(l) {
  return DEAD_TASK_STATUSES.includes(l.task_status);
}

// ---- Reads ------------------------------------------------------------------------------------

/** The board's lists with defaults: an older API without `short_supply` must not blank a tab. */
export function normalizeCoatingBoard(boardData) {
  const b = boardData || {};
  return {
    staging: b.staging || [],
    in_production: b.in_production || [],
    // Tasks the API refused to stage because the parts do not exist. Older API builds
    // do not send this key at all, so it must default rather than blank the tab.
    short_supply: b.short_supply || [],
    counts: b.counts || {},
  };
}

export const EMPTY_BOARD = Object.freeze(normalizeCoatingBoard(null));

/** GET /coating/board + GET /outside_processing_orders?include_lines=true. Resolves {board, opos}; throws an
 *  Error whose message is the API's sentence. */
export async function loadCoatingBoard(apiBase) {
  const [boardRes, opoRes] = await Promise.all([
    fetch(`${apiBase}/coating/board`),
    fetch(`${apiBase}/outside_processing_orders?include_lines=true`),
  ]);
  if (!boardRes.ok) throw new Error(await parseServerError(boardRes));
  if (!opoRes.ok) throw new Error(await parseServerError(opoRes));
  const boardData = await boardRes.json();
  const opoData = await opoRes.json();
  return { board: normalizeCoatingBoard(boardData), opos: Array.isArray(opoData) ? opoData : [] };
}

/** GET /service_vendors (the coaters the paper can name); [] on a failure (logged). */
export async function loadServiceVendors(apiBase) {
  try {
    const res = await fetch(`${apiBase}/service_vendors`);
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error('Failed to load service vendors:', err);
    return [];
  }
}

/** The ticked task ids still in the staging pool (batched elsewhere, voided... drop out); the same Set when
 *  nothing dropped. */
export function pruneSelection(prev, staging) {
  const live = new Set((staging || []).map((t) => t.task_id));
  const next = new Set([...prev].filter((id) => live.has(id)));
  return next.size === prev.size ? prev : next;
}

// ---- Derived lists ----------------------------------------------------------------------------

/** The manifests by tab: Drafts (task-linked only), at a vendor, and done. */
export function manifestBuckets(opos) {
  const list = opos || [];
  return {
    drafts: list.filter((o) => o.status === 'Draft' && isManifest(o)),
    atVendor: list.filter((o) => o.status === 'Sent' || o.status === 'Partially Returned'),
    history: list.filter((o) => ['Returned', 'Closed', 'Cancelled'].includes(o.status)),
  };
}

/**
 * Staging rows grouped by sales order (internal NULL-SO group per customer),
 * preserving the API's priority/due ordering. A job split by allocation is also listed
 * under each order its pieces are packed for (a share row, `_share`: not selectable, it
 * goes on that order's paper from its pallet button), and leaves its own order's group
 * once that order's share is on paper (Mike, 2026-10-27).
 */
export function stagingGroups(staging) {
  const map = new Map();
  const groupFor = (key, so) => {
    if (!map.has(key)) map.set(key, { key, ...so, tasks: [] });
    return map.get(key);
  };
  const rows = staging || [];
  rows.forEach((t) => {
    if (homeRowHidden(t)) return;
    const pallets = homeRowPallets(t);
    groupFor(groupKeyForTask(t), {
      sales_order_id: t.sales_order_id ?? null,
      sales_order_number: t.sales_order_number,
      customer_name: t.customer_name,
    }).tasks.push(pallets === t.pallets ? t : { ...t, pallets });
  });
  rows.forEach((t) => awaySharesToList(t).forEach((a) => {
    groupFor(`so:${a.sales_order_id}`, {
      sales_order_id: a.sales_order_id,
      sales_order_number: a.sales_order_number,
      customer_name: a.customer_name || '',
    }).tasks.push({ ...t, pallets: sharePallets(t, a), _share: a });
  }));
  return [...map.values()];
}

/** Pallet first (Mike, 2026-10-28): each group's pallets, the parts on each with one total, the jobs under it
 *  (./palletBuckets.js). Tasks on no pallet yet are listed one row each. Map group key → buckets. */
export function bucketsByGroup(groups) {
  const m = new Map();
  (groups || []).forEach((g) => m.set(g.key, palletBuckets(g.tasks, (t) => !ownShareDone(t))));
  return m;
}

/** Only badge the process when the queue actually holds more than one — a badge that
 *  says "Coating" on every row of an all-coating queue is noise, not information. */
export function hasMixedProcesses(groups) {
  return new Set(
    (groups || []).flatMap((g) => (g.tasks || []).map((t) => t.process_label).filter(Boolean))
  ).size > 1;
}

/** The ticked tasks' UNANIMOUS default vendor (prefills the create form), else null.
 *  Part/assembly coating vendors are DEFAULTS, not locks — mixed defaults are
 *  allowed in one selection; the office picks the actual coater at create. */
export function selectionVendorId(selectedTasks) {
  const ids = new Set((selectedTasks || []).map((t) => t.vendor_id).filter((v) => v != null));
  return ids.size === 1 ? [...ids][0] : null;
}

/** Draft manifests the selection could join (same SO; the draft's vendor wins over any task default — defaults
 *  are not locks). Internal (NULL-SO) customer identity is enforced server-side. */
export function matchingDrafts(drafts, selectedTasks) {
  if (!(selectedTasks || []).length) return [];
  const soId = selectedTasks[0].sales_order_id ?? null;
  return (drafts || []).filter((o) => (o.sales_order_id ?? null) === soId);
}

/** Selected tasks whose default vendor is not the chosen one: {count, names, chosenName} or null. */
export function vendorOverride(selectedTasks, vendorId, serviceVendors) {
  const chosen = vendorId ? Number(vendorId) : null;
  const overridden = chosen
    ? (selectedTasks || []).filter((t) => t.vendor_id != null && t.vendor_id !== chosen)
    : [];
  if (!overridden.length) return null;
  return {
    count: overridden.length,
    names: [...new Set(overridden.map((t) => t.vendor_name).filter(Boolean))],
    chosenName: (serviceVendors || []).find((v) => v.vendor_id === chosen)?.name || 'the selected vendor',
  };
}

/** The other pallets one tick takes with it (a job on both): their short ids. */
export function linkedPalletNames(buckets, p) {
  if (p.riderOnly) return [];
  return palletClosure(buckets.pallets, p.key).keys
    .filter((k) => k !== p.key)
    .map((k) => buckets.pallets.find((x) => x.key === k)?.short_id)
    .filter(Boolean);
}

/** What a pallet carries in all (packed), against what is still to send (`p.total`). */
export function palletPackedTotal(p) {
  return p.parts.reduce((n, x) => n + x.lines.reduce((m, l) => m + l.packed, 0), 0);
}

/** One job line's notes under a pallet's part: why its count differs from the job. */
export function jobLineNotes({ t, qty, packed, extras, share }) {
  const notes = [];
  if (packed - qty > 1e-9) notes.push(`${fmtQty(packed - qty)} of ${fmtQty(packed)} here already on papers`);
  if (extras > 0) notes.push(`+${fmtQty(extras)} extra${extras === 1 ? '' : 's'} not on a pallet`);
  if (!share) {
    (t.allocated_away || []).forEach((a) => {
      notes.push(`${fmtQty(a.qty)} more packed for ${a.sales_order_number || 'another order'}`);
    });
    if (Number(t.uncoated_extras) > 0) {
      notes.push(`${fmtQty(t.uncoated_extras)} extra${Number(t.uncoated_extras) === 1 ? '' : 's'} stay home`);
    }
  }
  return notes;
}

/** Every pallet ticked whole, across the groups: what the paper will carry, pallet by pallet. */
export function tickedPallets(groups, buckets, selectedTaskIds) {
  return (groups || []).flatMap((g) => (buckets.get(g.key)?.pallets || [])
    .filter((p) => palletTickState(p, selectedTaskIds) === 'all'));
}

/** The pallets an order's staged tasks sit on (Find all), unique ids. */
export function groupPalletIds(g) {
  return [...new Set(g.tasks.flatMap((t) => (t.pallets || []).map((pl) => pl.pallet_id)).filter(Boolean))];
}

// ---- Selection ----------------------------------------------------------------------------------
// Each answers {ids} (the new Set of ticked task ids) or {refuse} (the sentence; nothing changes).

export function mixedGroupsMessage(existingKey, newKey) {
  if (existingKey.startsWith('internal:') && newKey.startsWith('internal:')) {
    return 'One manifest per customer for internal jobs — finish this customer first.';
  }
  return 'One manifest per sales order — finish this SO first.';
}

export function toggleTaskIds(selectedIds, selectedTasks, task) {
  if (selectedIds.has(task.task_id)) {
    const next = new Set(selectedIds);
    next.delete(task.task_id);
    return { ids: next };
  }
  if (selectedTasks.length) {
    const existingKey = groupKeyForTask(selectedTasks[0]);
    const newKey = groupKeyForTask(task);
    if (existingKey !== newKey) return { refuse: mixedGroupsMessage(existingKey, newKey) };
  }
  const next = new Set(selectedIds);
  next.add(task.task_id);
  return { ids: next };
}

export function toggleGroupIds(selectedIds, selectedTasks, group, checked) {
  if (!checked) {
    const next = new Set(selectedIds);
    group.tasks.forEach((t) => { if (!t._share) next.delete(t.task_id); });
    return { ids: next };
  }
  const outsiders = selectedTasks.filter((t) => groupKeyForTask(t) !== group.key);
  if (outsiders.length) return { refuse: mixedGroupsMessage(groupKeyForTask(outsiders[0]), group.key) };
  const next = new Set(selectedIds);
  group.tasks.forEach((t) => { if (!t._share && !ownShareDone(t)) next.add(t.task_id); });
  return { ids: next };
}

/** Tick / untick a pallet: its own tasks, and every pallet sharing one of them (one paper carries a task's
 *  pieces on all its pallets). */
export function togglePalletIds(selectedIds, selectedTasks, group, pallets, key, checked) {
  const { taskIds } = palletClosure(pallets, key);
  if (checked) {
    const outsiders = selectedTasks.filter((t) => groupKeyForTask(t) !== group.key);
    if (outsiders.length) return { refuse: mixedGroupsMessage(groupKeyForTask(outsiders[0]), group.key) };
    const next = new Set(selectedIds);
    taskIds.forEach((id) => next.add(id));
    return { ids: next };
  }
  const next = new Set(selectedIds);
  taskIds.forEach((id) => next.delete(id));
  return { ids: next };
}

/** A group's own tickable tasks and whether all of them are ticked (the group's select-all). */
export function groupSelection(group, selectedIds) {
  const selectable = group.tasks.filter((t) => !t._share && !ownShareDone(t));
  return {
    selectable,
    allSelected: selectable.length > 0 && selectable.every((t) => selectedIds.has(t.task_id)),
  };
}

// ---- Receive ----------------------------------------------------------------------------------

/** What is still out on a line: sent less returned and scrapped (never below 0). */
export function lineRemaining(l) {
  return Math.max(
    0,
    Number(l.quantity_sent || 0) - Number(l.quantity_returned || 0) - Number(l.quantity_scrapped || 0)
  );
}

/** Back so far on a line: returned + scrapped. */
export function lineBack(l) {
  return Number(l.quantity_returned || 0) + Number(l.quantity_scrapped || 0);
}

/** The receive form's rows, prefilled with each line's remaining: {[opo_line_id]: {ret, scr, close}}. */
export function receiveRowsFor(opo) {
  const rows = {};
  (opo.lines || []).forEach((l) => {
    rows[l.opo_line_id] = { ret: fmtQty(lineRemaining(l)), scr: '0', close: false };
  });
  return rows;
}

/**
 * The receive form's office fields, prefilled from the committed estimate rather than blank. Blank + "(optional)"
 * is why 15 of 17 outside-processing orders in prod had never had actual_cost set: the operator has no reason to
 * type anything, the manifest goes Returned, and the SO's accrued cost never moves off the estimate.
 */
export function receiveOfficePrefill(opo) {
  return {
    invoice: opo.vendor_invoice_number || '',
    cost: opo.actual_cost != null ? String(opo.actual_cost)
      : (opo.estimated_cost != null ? String(opo.estimated_cost) : ''),
    freight: opo.freight_cost != null && Number(opo.freight_cost) > 0 ? String(opo.freight_cost) : '',
  };
}

/** The receive request's lines from the form rows: {lines} or {error} (the sentence to show). */
export function receiveLinesFrom(opo, rows) {
  const lines = [];
  for (const l of opo.lines || []) {
    const row = rows[l.opo_line_id] || {};
    const ret = parseFloat(row.ret);
    const scr = parseFloat(row.scr);
    const retNow = isNaN(ret) ? 0 : ret;
    const scrNow = isNaN(scr) ? 0 : scr;
    const remaining = lineRemaining(l);
    const label = l.description || l.part_product_id || `line ${l.line_number}`;
    if (retNow < 0 || scrNow < 0) {
      return { error: `${label}: quantities cannot be negative.` };
    }
    if (retNow + scrNow > remaining + 1e-9) {
      return {
        error: `${label}: received + scrapped now (${fmtQty(retNow + scrNow)}) exceeds the remaining balance (${fmtQty(remaining)}).`,
      };
    }
    if (retNow > 0 || scrNow > 0 || row.close) {
      lines.push({
        opo_line_id: l.opo_line_id,
        quantity_returned: retNow,
        quantity_scrapped: scrNow,
        close_line: !!row.close,
      });
    }
  }
  if (!lines.length) {
    return { error: 'Enter a received or scrapped quantity (or Close short) on at least one line.' };
  }
  return { lines };
}

/** The At vendor card's facts: days out, past due. */
export function atVendorFacts(opo, now = Date.now()) {
  const sentMs = opo.sent_at ? new Date(opo.sent_at).getTime() : null;
  return {
    daysOut: sentMs ? Math.max(0, Math.floor((now - sentMs) / 86400000)) : null,
    pastDue: opo.due_back ? new Date(opo.due_back).getTime() < now : false,
  };
}

// ---- Documents ------------------------------------------------------------------------------------

/** The paper (the manifest PDF that travels with the parts): {url, filename}. */
export function paperDocument(apiBase, opo) {
  return { url: `${apiBase}/outside_processing_orders/${opo.opo_id}/pdf`, filename: `${opo.opo_number || `OPO_${opo.opo_id}`}.pdf` };
}

/** The 4x6 pallet labels PDF path (one label per pallet on the manifest; jobq-api "Outsourcing pallet labels"). */
export function palletLabelsPath(opo) {
  return `/outside_processing_orders/${opo.opo_id}/pallet_labels.pdf`;
}

/** A manifest line's identity, assembly first (legacy per-task lines were written part-first, "FAB311916 - 17x8.33
 *  plate", though the batch is WLD304072 assemblies): {assembly, text, job} (job = null when the text names it). */
export function lineIdentity(l) {
  const text = l.description || l.part_product_id || '—';
  const job = l.master_job_num && !(l.description || '').includes(`Job ${l.master_job_num}`) ? l.master_job_num : null;
  return { assembly: l.line_assembly_name || null, text, job };
}

/**
 * A split job's other orders, one line each (jobq-api `allocated_away`; CLAUDE.md "Outsourcing staging: a job split by
 * allocation"): "↪ 93 for SO-1139 on PLT-42C7 (listed under SO-1139)". The desktop staging table's words.
 */
export function allocationLines(t) {
  if (!t || !Array.isArray(t.allocated_away)) return [];
  return t.allocated_away.map((a) => {
    const so = a.sales_order_number || `SO #${a.sales_order_id}`;
    const on = (a.pallets || []).length ? ` on ${a.pallets.join(', ')}` : '';
    const where = Number(a.remaining) <= 0 ? 'on its paper'
      : a.sales_order_id != null ? `listed under ${a.sales_order_number || 'that order'}` : 'its pallet button';
    return `↪ ${fmtQty(a.qty)} for ${so}${on} (${where})`;
  });
}

/** A part cut on another order's job and allocated here, when only some of it is this order's: "12 for this SO · 4 surplus". */
export function crossSoShareLine(t) {
  if (!t || !t.cross_so_allocated || t.allocated_qty == null || t.assembly_units) return null;
  const total = Number(t.units || t.quantity);
  if (!(Number(t.allocated_qty) < total)) return null;
  return `${fmtQty(t.allocated_qty)} for this SO · ${fmtQty(total - Number(t.allocated_qty))} surplus`;
}
